import type { MetroSpt } from '../../api/spt';

/**
 * As contas puras do perfil do furo SPT — onda 357 (RN-276, D7). A tela e o
 * boletim em PDF desenham na mesma lógica: profundidade verdadeira, o ensaio
 * do metro *d* na faixa de *d* a *d* + 1 (ele vai de *d* a *d* + 0,45 m), e o
 * gráfico pelo Critério 1 — penetração diferente de 30 cm levada a 30 cm só
 * no desenho; acima de 50, o ponto fica na borda com seta e a fração.
 */

/** O teto do gráfico SPT (a escala vai de 0 a 50 golpes). */
export const TETO_DO_GRAFICO = 50;

/** Onde o ponto do metro *d* fica no eixo da profundidade, em metros. */
export const profDoPonto = (d: number) => d + 0.55;

export interface PontoDoGrafico { prof: number; valor: number; alem: boolean; rotulo: string }

/** Os pontos de N' (`ini`) e de N (`fin`) — os valores do banco (fn_spt_n), cortados no teto. */
export function pontosDoGrafico(metros: MetroSpt[]): { ini: PontoDoGrafico[]; fin: PontoDoGrafico[] } {
  const ini: PontoDoGrafico[] = [];
  const fin: PontoDoGrafico[] = [];
  for (const m of metros) {
    const p = profDoPonto(m.profundidade);
    if (m.n_ini_valor != null) {
      ini.push({ prof: p, valor: Math.min(m.n_ini_valor, TETO_DO_GRAFICO), alem: m.n_ini_valor > TETO_DO_GRAFICO, rotulo: m.n_ini_texto });
    }
    if (m.n_valor != null) {
      fin.push({ prof: p, valor: Math.min(m.n_valor, TETO_DO_GRAFICO), alem: m.n_valor > TETO_DO_GRAFICO, rotulo: m.n_texto });
    }
  }
  return { ini, fin };
}

/** O limite arrastado: de 5 em 5 cm, entre as vizinhas (topo + 0,05 e próxima − 0,05). */
export function baseArrastada(prof: number, min: number, max: number): number {
  const p = Math.round(prof / 0.05) * 0.05;
  return Math.round(Math.min(max - 0.05, Math.max(min + 0.05, p)) * 100) / 100;
}

/** O nível d'água arrastado: de 5 em 5 cm, de 0 ao fim do furo. */
export function naArrastado(prof: number, fim: number): number {
  return Math.round(Math.min(fim, Math.max(0, Math.round(prof / 0.05) * 0.05)) * 100) / 100;
}

/** As linhas da tabela: os metros lançados e um em branco, o próximo, para digitar em seguida. */
export function linhasDaTabela(metros: MetroSpt[], fim: number | null): { prof: number; m: MetroSpt | null }[] {
  const linhas: { prof: number; m: MetroSpt | null }[] = metros.map((m) => ({ prof: m.profundidade, m }));
  const ultimo = metros.length ? metros[metros.length - 1].profundidade : 0;
  const proximo = Math.floor(ultimo) + 1;
  if (fim == null || proximo + 0.45 <= fim + 0.001) linhas.push({ prof: proximo, m: null });
  return linhas;
}

/**
 * O texto que não cabe na camada — onda 361 (opção A do Humberto, 30/10/2026). A mesma conta do boletim em PDF
 * (`api/src/documentos/spt-boletim.ts`, `arrumarTextos`): a letra encolhe até o mínimo; se nem assim couber, o texto
 * desce para o espaço livre com uma chamada até a camada, e as descrições seguintes descem junto. Nunca corta.
 */
export interface TextoArrumado { topo: number; altura: number; fonte: number; chamada: boolean }
export interface OpcoesDoArranjo {
  largura: number; fonteMax: number; fonteMin: number; entrelinha: number; folga: number; folgaX?: number; passo?: number; limite?: number;
}
export function linhasDoTexto(texto: string, fonte: number, largura: number): number {
  const cabem = Math.max(1, Math.floor(largura / (fonte * 0.62)));
  let linhas = 0;
  for (const paragrafo of texto.split('\n')) {
    let atual = 0;
    linhas += 1;
    for (const palavra of paragrafo.split(/\s+/).filter(Boolean)) {
      const n = palavra.length;
      if (atual === 0) { linhas += Math.floor((n - 1) / cabem); atual = ((n - 1) % cabem) + 1; continue; }
      if (atual + 1 + n <= cabem) atual += 1 + n;
      else { linhas += 1 + Math.floor((n - 1) / cabem); atual = ((n - 1) % cabem) + 1; }
    }
  }
  return linhas;
}
export function arrumarTextos(itens: { topo: number; base: number; texto: string }[], o: OpcoesDoArranjo): TextoArrumado[] {
  const passo = o.passo ?? 0.5;
  const alturaCom = (t: string, f: number) => linhasDoTexto(t, f, o.largura - 2 * (o.folgaX ?? o.folga)) * f * o.entrelinha + 2 * o.folga;
  let fundo = -Infinity;
  return itens.map((it) => {
    const h = it.base - it.topo;
    let fonte = o.fonteMax;
    let altura = alturaCom(it.texto, fonte);
    while (altura > h && fonte - passo >= o.fonteMin - 1e-9) {
      fonte = Math.round((fonte - passo) * 1000) / 1000;
      altura = alturaCom(it.texto, fonte);
    }
    if (altura > h) { fonte = o.fonteMin; altura = alturaCom(it.texto, fonte); }
    let topo = altura <= h ? it.topo + (h - altura) / 2 : it.topo;
    topo = Math.max(topo, fundo);
    if (o.limite != null && topo + altura > o.limite) topo = Math.max(it.topo, o.limite - altura);
    fundo = topo + altura;
    const chamada = topo < it.topo - 1e-6 || topo + altura > it.base + 1e-6;
    return { topo, altura, fonte, chamada };
  });
}
